import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, EmptyState, Chip, LoadingState, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Plus, X, CheckCircle, XCircle, Users, ListChecks, Question, CalendarBlank, Eye, EyeSlash,
    Trash, Globe, Buildings, HouseLine, UsersThree, PencilSimple, Lock, Trophy, Scales,
    MagnifyingGlass, ChartBar, Camera, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const VOTE_TYPES = [
    { value: "election", label: "Committee Election", icon: Users },
    { value: "poll", label: "Poll", icon: Question },
    { value: "resolution", label: "Resolution", icon: ListChecks },
];
const PHASE_META = {
    draft: { label: "Draft", variant: "neutral" },
    upcoming: { label: "Upcoming", variant: "warn" },
    active: { label: "Active", variant: "success" },
    closed: { label: "Closed", variant: "default" },
    cancelled: { label: "Cancelled", variant: "danger" },
};
const AUDIENCE_ICON = { ALL: Globe, BLOCK: Buildings, FLAT: HouseLine, COMMITTEE_ADMIN: UsersThree };
const AUDIENCE_LABEL_BASE = { ALL: "Everyone", BLOCK: "Block", FLAT: "Flat", COMMITTEE_ADMIN: "Committee & Admin" };

function typeInfo(t) { return VOTE_TYPES.find(v => v.value === t) || VOTE_TYPES[0]; }
function fmt(dt) {
    if (!dt) return "—";
    try { return new Date(dt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return dt; }
}
function toInputDT(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const off = d.getTimezoneOffset();
        return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
    } catch { return ""; }
}
function audienceLabel(aud, flats) {
    if (!aud) return "Everyone";
    if (aud.type === "BLOCK") return `Block ${aud.block}`;
    if (aud.type === "FLAT") {
        const f = flats.find(x => x.id === aud.flat_id);
        return f ? `Flat ${f.block}-${f.number}` : "Specific Flat";
    }
    return AUDIENCE_LABEL_BASE[aud.type] || "Everyone";
}

// ---------- Candidate avatar (secure blob fetch for saved photos, local preview for staged uploads) ----------
function CandidateAvatar({ voteId, candidateId, photoFileId, localPreview, name, size = 44 }) {
    const [src, setSrc] = useState(null);
    const [err, setErr] = useState(false);
    useEffect(() => {
        if (localPreview || !photoFileId || !voteId || !candidateId) return;
        let revoked = null, cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/votes/${voteId}/candidates/${candidateId}/photo`, { responseType: "blob" });
                if (cancelled) return;
                const url = URL.createObjectURL(res.data);
                revoked = url;
                setSrc(url);
            } catch { if (!cancelled) setErr(true); }
        })();
        return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
    }, [voteId, candidateId, photoFileId, localPreview]);
    const finalSrc = localPreview || (!err ? src : null);
    if (!finalSrc) {
        return (
            <div style={{ width: size, height: size }} className="rounded-full bg-brand-sage text-brand-ink font-heading flex items-center justify-center shrink-0 text-sm">
                {(name || "?").charAt(0).toUpperCase()}
            </div>
        );
    }
    return <img src={finalSrc} alt={name || "candidate"} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0 border border-brand-line" />;
}

// ---------- Results Dialog ----------
function ResultsDialog({ vote, onClose, onToggleShowResults }) {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try { const r = await api.get(`/votes/${vote.id}/results`); setResults(r.data); }
        catch (e) { toast.error(formatError(e)); onClose?.(); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [vote.id]);

    if (loading || !results) return <LoadingState message="Loading results..." />;
    const choices = vote.type === "election" ? (vote.candidates || []) : (vote.options || []);

    return (
        <div className="space-y-5" data-testid="vote-results-dialog">
            <div className="grid grid-cols-3 gap-3 text-center">
                <StatCard label="Total Votes" value={results.total_votes} testId="results-total-votes" />
                <StatCard label="Participation" value={`${results.participation_rate}%`} accent testId="results-participation" />
                <StatCard label="Eligible Flats" value={results.eligible_flats} testId="results-eligible-flats" />
            </div>

            {vote.type === "election" && results.phase === "closed" && (
                results.is_tie ? (
                    <div className="rounded-lg px-4 py-3 bg-[#FFF7ED] text-[#EA580C] border border-[#FFEDD5] flex items-center gap-2 text-sm font-medium" data-testid="results-tie-banner">
                        <Scales size={18} weight="fill" /> Tie — No winner determined.
                    </div>
                ) : results.winner_candidate_id ? (
                    <div className="rounded-lg px-4 py-3 bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0] flex items-center gap-2 text-sm font-medium" data-testid="results-winner-banner">
                        <Trophy size={18} weight="fill" />
                        Winner: {choices.find(c => c.id === results.winner_candidate_id)?.name || "—"}
                    </div>
                ) : (
                    <div className="rounded-sm px-4 py-3 bg-brand-bg text-brand-inkSoft text-sm" data-testid="results-no-votes-banner">No votes were cast in this election.</div>
                )
            )}

            {vote.type === "resolution" && results.resolution && (
                <div className={`rounded-lg px-4 py-3 flex items-center justify-between text-sm font-medium ${
                    results.resolution.outcome === "passed" ? "bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0]" :
                    results.resolution.outcome === "failed" ? "bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]" :
                    "bg-brand-bg text-brand-inkSoft border border-brand-line"
                }`} data-testid="results-resolution-outcome">
                    <span className="capitalize">Outcome: {results.resolution.outcome}</span>
                    {results.resolution.threshold_type && (
                        <span className="text-xs opacity-80">
                            {results.resolution.threshold_type === "super_majority"
                                ? `Requires >${results.resolution.threshold_percentage}% YES`
                                : "Simple majority (YES > NO)"}
                        </span>
                    )}
                </div>
            )}

            <div className="space-y-2">
                {choices.map(c => {
                    const r = results.results[c.id] || { count: 0, percentage: 0 };
                    const isWinner = vote.type === "election" && results.winner_candidate_id === c.id;
                    return (
                        <div key={c.id} className="bg-brand-bg border border-brand-line rounded-sm p-3" data-testid={`result-row-${c.id}`}>
                            <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2 min-w-0">
                                    {vote.type === "election" && (
                                        <CandidateAvatar voteId={vote.id} candidateId={c.id} photoFileId={c.photo_file_id} name={c.name} size={28} />
                                    )}
                                    <span className="font-medium text-brand-ink text-sm truncate">
                                        {vote.type === "election" ? c.name : c.text}
                                    </span>
                                    {vote.type === "election" && c.position && <span className="text-xs text-brand-inkSoft shrink-0">({c.position})</span>}
                                    {isWinner && <Trophy size={14} weight="fill" className="text-brand-action shrink-0" />}
                                </div>
                                <span className="text-xs text-brand-inkSoft shrink-0">{r.count} votes ({r.percentage}%)</span>
                            </div>
                            <div className="w-full bg-white rounded-full h-2 overflow-hidden">
                                <div className="bg-brand-action h-2 rounded-full transition-all" style={{ width: `${r.percentage}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center justify-between border-t border-brand-line pt-4">
                <div className="text-xs text-brand-inkSoft">
                    {vote.show_results_before_end ? "Live results are visible to residents." : "Results are hidden from residents until the vote closes."}
                </div>
                {onToggleShowResults && (
                    <Button size="sm" variant="outline" className="rounded-full border-brand-line" data-testid="results-toggle-visibility-btn"
                        onClick={() => onToggleShowResults(vote)}>
                        {vote.show_results_before_end ? <><EyeSlash size={14} className="mr-1" /> Hide from residents</> : <><Eye size={14} className="mr-1" /> Show to residents now</>}
                    </Button>
                )}
            </div>
        </div>
    );
}

// ---------- Create/Edit Dialog ----------
function VoteFormDialog({ open, onOpenChange, editing, users, flats, onSaved }) {
    const emptyForm = () => ({
        title: "", description: "", type: "election", start_date: "", end_date: "",
        audienceType: "ALL", block: "", flat_id: "",
        candidates: [], max_selections: 1,
        options: [{ text: "" }, { text: "" }],
        allow_abstain: true, threshold_type: "simple_majority", threshold_percentage: 60,
        show_results_before_end: false,
    });
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const isEdit = !!editing;
    const locked = isEdit && (editing.total_votes || 0) > 0;

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setForm({
                title: editing.title, description: editing.description, type: editing.type,
                start_date: toInputDT(editing.start_date), end_date: toInputDT(editing.end_date),
                audienceType: editing.audience?.type || "ALL", block: editing.audience?.block || "", flat_id: editing.audience?.flat_id || "",
                candidates: (editing.candidates || []).map(c => ({ ...c, photo_preview_url: null })),
                max_selections: editing.max_selections || 1,
                options: (editing.options || []).map(o => ({ text: o.text })),
                allow_abstain: editing.allow_abstain ?? true,
                threshold_type: editing.threshold_type || "simple_majority",
                threshold_percentage: editing.threshold_percentage || 60,
                show_results_before_end: !!editing.show_results_before_end,
            });
        } else {
            setForm(emptyForm());
        }
    }, [open, editing]);

    const blocksList = useMemo(() => Array.from(new Set(flats.map(f => f.block).filter(Boolean))).sort(), [flats]);
    const flatsForBlock = useMemo(() => flats.filter(f => f.block === form.block).sort((a, b) => (a.number || "").localeCompare(b.number || "")), [flats, form.block]);
    const eligibleCandidateUsers = useMemo(() => users.filter(u => u.flat && u.flat.id), [users]);

    function buildAudience() {
        if (form.audienceType === "ALL" || form.audienceType === "COMMITTEE_ADMIN") return { type: form.audienceType };
        if (form.audienceType === "BLOCK") return { type: "BLOCK", block: form.block };
        return { type: "FLAT", flat_id: form.flat_id };
    }

    function addCandidate() {
        setForm({ ...form, candidates: [...form.candidates, { user_id: "", position: "", bio: "", photo_file_id: null, photo_preview_url: null }] });
    }
    function removeCandidate(idx) {
        const updated = [...form.candidates]; updated.splice(idx, 1);
        setForm({ ...form, candidates: updated, max_selections: Math.min(form.max_selections, Math.max(1, updated.length)) });
    }
    function updateCandidate(idx, field, value) {
        const updated = [...form.candidates]; updated[idx] = { ...updated[idx], [field]: value };
        setForm({ ...form, candidates: updated });
    }
    async function handleCandidatePhoto(idx, file) {
        if (!file) return;
        const previewUrl = URL.createObjectURL(file);
        updateCandidate(idx, "photo_preview_url", previewUrl);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            updateCandidate(idx, "photo_file_id", data.id);
        } catch (e) { toast.error(`Photo upload failed: ${formatError(e)}`); }
    }

    function addOption() { setForm({ ...form, options: [...form.options, { text: "" }] }); }
    function removeOption(idx) { const u = [...form.options]; u.splice(idx, 1); setForm({ ...form, options: u }); }
    function updateOption(idx, value) { const u = [...form.options]; u[idx] = { text: value }; setForm({ ...form, options: u }); }

    function validate() {
        if (!form.title.trim()) { toast.error("Title cannot be empty"); return false; }
        if (!form.description.trim()) { toast.error("Description cannot be empty"); return false; }
        if (!locked) {
            if (!form.start_date || !form.end_date) { toast.error("Start and end date/time are required"); return false; }
            if (new Date(form.end_date) <= new Date(form.start_date)) { toast.error("End date must be after start date"); return false; }
            if (form.audienceType === "BLOCK" && !form.block) { toast.error("Please select a block"); return false; }
            if (form.audienceType === "FLAT" && !form.flat_id) { toast.error("Please select a flat"); return false; }
            if (form.type === "election") {
                if (form.candidates.length < 1) { toast.error("An election requires at least 1 candidate"); return false; }
                if (form.candidates.some(c => !c.user_id)) { toast.error("Please select a resident for every candidate"); return false; }
                const ids = form.candidates.map(c => c.user_id);
                if (new Set(ids).size !== ids.length) { toast.error("Each resident can only be nominated once"); return false; }
                if (form.max_selections < 1 || form.max_selections > form.candidates.length) { toast.error(`Max selections must be between 1 and ${form.candidates.length}`); return false; }
            } else if (form.type === "poll") {
                const texts = form.options.map(o => o.text.trim()).filter(Boolean);
                if (texts.length < 2) { toast.error("A poll requires at least 2 options"); return false; }
                if (new Set(texts.map(t => t.toLowerCase())).size !== texts.length) { toast.error("Poll options must be unique"); return false; }
            } else if (form.type === "resolution") {
                if (form.threshold_type === "super_majority" && !(form.threshold_percentage > 50 && form.threshold_percentage <= 100)) {
                    toast.error("Super-majority threshold must be greater than 50 and up to 100"); return false;
                }
            }
        }
        return true;
    }

    async function submit(publishNow) {
        if (!validate()) return;
        setSaving(true);
        try {
            const base = { title: form.title.trim(), description: form.description.trim(), show_results_before_end: form.show_results_before_end };
            let payload = { ...base };
            if (!locked) {
                payload = {
                    ...payload,
                    start_date: new Date(form.start_date).toISOString(),
                    end_date: new Date(form.end_date).toISOString(),
                    audience: buildAudience(),
                };
                if (form.type === "election") {
                    payload.candidates = form.candidates.map(c => ({ user_id: c.user_id, position: c.position || null, bio: c.bio || null, photo_file_id: c.photo_file_id || null }));
                    payload.max_selections = form.max_selections;
                } else if (form.type === "poll") {
                    payload.options = form.options.filter(o => o.text.trim()).map(o => ({ text: o.text.trim() }));
                } else {
                    payload.allow_abstain = form.allow_abstain;
                    payload.threshold_type = form.threshold_type;
                    payload.threshold_percentage = form.threshold_type === "super_majority" ? form.threshold_percentage : null;
                }
            }
            if (isEdit) {
                await api.put(`/votes/${editing.id}`, payload);
                toast.success("Vote updated");
            } else {
                payload.type = form.type;
                const r = await api.post("/votes", payload);
                if (publishNow) {
                    await api.post(`/votes/${r.data.id}/publish`);
                    toast.success("Vote created and published");
                } else {
                    toast.success("Draft saved");
                }
            }
            onOpenChange(false);
            onSaved?.();
        } catch (e) { toast.error(formatError(e)); }
        finally { setSaving(false); }
    }

    const TypeIcon = typeInfo(form.type).icon;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
                        <TypeIcon size={22} className="text-brand-action" /> {isEdit ? "Edit Vote" : "Create New Vote"}
                    </DialogTitle>
                    <DialogDescription>{isEdit ? "Update details. Ballot-integrity fields lock once someone has voted." : "Saved as a draft first — publish separately when ready."}</DialogDescription>
                </DialogHeader>

                {locked && (
                    <div className="rounded-lg px-3 py-2 bg-[#FFF7ED] text-[#EA580C] border border-[#FFEDD5] text-xs flex items-center gap-1.5" data-testid="vote-form-locked-banner">
                        <Lock size={13} weight="fill" /> Ballots have been cast — dates, audience, candidates/options are locked. Title, description and result visibility can still be changed.
                    </div>
                )}

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label>Vote Type *</Label>
                        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} disabled={isEdit}>
                            <SelectTrigger data-testid="vote-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                            <SelectContent>{VOTE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                        {isEdit && <p className="text-[10px] text-brand-inkSoft">Vote type cannot be changed after creation.</p>}
                    </div>

                    <div className="space-y-1.5">
                        <Label>Title *</Label>
                        <Input data-testid="vote-title-input" required placeholder="e.g., Annual Committee Election 2026" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm border-brand-line" />
                    </div>

                    <div className="space-y-1.5">
                        <Label>Description *</Label>
                        <Textarea data-testid="vote-description-input" required placeholder="Provide details about this vote..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-sm border-brand-line" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Start Date & Time *</Label>
                            <Input data-testid="vote-start-date-input" type="datetime-local" disabled={locked} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>End Date & Time *</Label>
                            <Input data-testid="vote-end-date-input" type="datetime-local" disabled={locked} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                    </div>

                    <div className="space-y-2 border border-brand-line rounded-sm p-3 bg-brand-bg" data-testid="vote-audience-picker">
                        <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Target Audience</Label>
                        <Select value={form.audienceType} disabled={locked} onValueChange={(v) => setForm({ ...form, audienceType: v, block: "", flat_id: "" })}>
                            <SelectTrigger data-testid="vote-audience-type-select" className="rounded-sm border-brand-line bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ALL">Everyone</SelectItem>
                                <SelectItem value="BLOCK">Specific Block</SelectItem>
                                <SelectItem value="FLAT">Specific Flat</SelectItem>
                                <SelectItem value="COMMITTEE_ADMIN">Committee & Admin</SelectItem>
                            </SelectContent>
                        </Select>
                        {form.audienceType === "BLOCK" && (
                            <Select value={form.block} disabled={locked} onValueChange={(v) => setForm({ ...form, block: v })}>
                                <SelectTrigger data-testid="vote-audience-block-select" className="rounded-sm border-brand-line bg-white"><SelectValue placeholder="Pick block" /></SelectTrigger>
                                <SelectContent>{blocksList.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                            </Select>
                        )}
                        {form.audienceType === "FLAT" && (
                            <div className="grid grid-cols-2 gap-2">
                                <Select value={form.block} disabled={locked} onValueChange={(v) => setForm({ ...form, block: v, flat_id: "" })}>
                                    <SelectTrigger data-testid="vote-audience-flat-block-select" className="rounded-sm border-brand-line bg-white"><SelectValue placeholder="Block" /></SelectTrigger>
                                    <SelectContent>{blocksList.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                                </Select>
                                <Select value={form.flat_id} disabled={locked} onValueChange={(v) => setForm({ ...form, flat_id: v })}>
                                    <SelectTrigger data-testid="vote-audience-flat-select" className="rounded-sm border-brand-line bg-white"><SelectValue placeholder={form.block ? "Pick flat" : "Pick block first"} /></SelectTrigger>
                                    <SelectContent>{flatsForBlock.map(f => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        )}
                        <p className="text-[10px] text-brand-inkSoft">Vacant flats (no linked resident) are automatically excluded from eligibility.</p>
                    </div>

                    {/* Election */}
                    {form.type === "election" && (
                        <div className="border-t border-brand-line pt-4 space-y-3" data-testid="vote-candidates-section">
                            <div className="flex justify-between items-center">
                                <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Candidates</Label>
                                {!locked && <Button type="button" size="sm" variant="outline" className="rounded-full border-brand-line" onClick={addCandidate} data-testid="vote-add-candidate-btn"><Plus size={14} className="mr-1" /> Add Candidate</Button>}
                            </div>

                            {form.candidates.length === 0 && !locked && <p className="text-xs text-brand-inkSoft">No candidates yet — add at least one.</p>}

                            {form.candidates.map((candidate, idx) => (
                                <div key={idx} className="bg-brand-bg border border-brand-line rounded-sm p-3 space-y-2" data-testid={`vote-candidate-row-${idx}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="relative shrink-0">
                                            <CandidateAvatar voteId={editing?.id} candidateId={candidate.id} photoFileId={candidate.photo_file_id} localPreview={candidate.photo_preview_url} name={candidate.name} size={48} />
                                            {!locked && (
                                                <label className="absolute -bottom-1 -right-1 bg-brand-action text-white rounded-full p-1 cursor-pointer" data-testid={`vote-candidate-photo-upload-${idx}`}>
                                                    <Camera size={11} weight="fill" />
                                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleCandidatePhoto(idx, e.target.files[0])} />
                                                </label>
                                            )}
                                        </div>
                                        <div className="flex-1 space-y-2 min-w-0">
                                            <div className="flex justify-between items-center gap-2">
                                                <Select value={candidate.user_id} disabled={locked} onValueChange={(v) => updateCandidate(idx, "user_id", v)}>
                                                    <SelectTrigger data-testid={`vote-candidate-user-select-${idx}`} className="rounded-sm border-brand-line bg-white text-sm"><SelectValue placeholder="Select resident..." /></SelectTrigger>
                                                    <SelectContent>
                                                        {eligibleCandidateUsers.length === 0 ? (
                                                            <div className="p-2 text-xs text-brand-inkSoft">No residents with a flat found.</div>
                                                        ) : eligibleCandidateUsers.map(u => (
                                                            <SelectItem key={u.id} value={u.id}>{u.name} — {u.flat.block}-{u.flat.number}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {!locked && (
                                                    <button type="button" onClick={() => removeCandidate(idx)} className="text-brand-inkSoft hover:text-brand-action shrink-0" data-testid={`vote-remove-candidate-btn-${idx}`}><X size={16} /></button>
                                                )}
                                            </div>
                                            <Input data-testid={`vote-candidate-position-input-${idx}`} disabled={locked} placeholder="Position (e.g. Secretary) — optional" value={candidate.position || ""} onChange={(e) => updateCandidate(idx, "position", e.target.value)} className="rounded-sm border-brand-line bg-white text-sm" />
                                            <Textarea data-testid={`vote-candidate-bio-input-${idx}`} disabled={locked} placeholder="Short bio — optional" rows={2} value={candidate.bio || ""} onChange={(e) => updateCandidate(idx, "bio", e.target.value)} className="rounded-sm border-brand-line bg-white text-sm" />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {form.candidates.length > 0 && (
                                <div className="space-y-1.5">
                                    <Label>Max Selections Per Ballot</Label>
                                    <Input data-testid="vote-max-selections-input" type="number" min="1" max={form.candidates.length} disabled={locked} value={form.max_selections} onChange={(e) => setForm({ ...form, max_selections: parseInt(e.target.value) || 1 })} className="rounded-sm border-brand-line w-28" />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Poll */}
                    {form.type === "poll" && (
                        <div className="border-t border-brand-line pt-4 space-y-3" data-testid="vote-options-section">
                            <div className="flex justify-between items-center">
                                <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Options (min. 2)</Label>
                                {!locked && <Button type="button" size="sm" variant="outline" className="rounded-full border-brand-line" onClick={addOption} data-testid="vote-add-option-btn"><Plus size={14} className="mr-1" /> Add Option</Button>}
                            </div>
                            {form.options.map((option, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                    <Input data-testid={`vote-option-input-${idx}`} disabled={locked} placeholder={`Option ${idx + 1}`} value={option.text} onChange={(e) => updateOption(idx, e.target.value)} className="rounded-sm border-brand-line" />
                                    {!locked && form.options.length > 2 && (
                                        <button type="button" onClick={() => removeOption(idx)} className="text-brand-inkSoft hover:text-brand-action" data-testid={`vote-remove-option-btn-${idx}`}><X size={16} /></button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Resolution */}
                    {form.type === "resolution" && (
                        <div className="border-t border-brand-line pt-4 space-y-3" data-testid="vote-resolution-section">
                            <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Resolution Settings</Label>
                            <div className="flex items-center justify-between bg-brand-bg border border-brand-line rounded-sm p-3">
                                <div>
                                    <p className="text-sm text-brand-ink font-medium">Allow Abstain</p>
                                    <p className="text-xs text-brand-inkSoft">Abstain counts toward participation but not toward pass/fail.</p>
                                </div>
                                <Switch data-testid="vote-allow-abstain-switch" checked={form.allow_abstain} disabled={locked} onCheckedChange={(v) => setForm({ ...form, allow_abstain: v })} />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Pass Threshold</Label>
                                <Select value={form.threshold_type} disabled={locked} onValueChange={(v) => setForm({ ...form, threshold_type: v })}>
                                    <SelectTrigger data-testid="vote-threshold-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="simple_majority">Simple Majority (YES &gt; NO)</SelectItem>
                                        <SelectItem value="super_majority">Super Majority (custom %)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {form.threshold_type === "super_majority" && (
                                <div className="space-y-1.5">
                                    <Label>Threshold Percentage (&gt;50 – 100)</Label>
                                    <Input data-testid="vote-threshold-percentage-input" type="number" min="51" max="100" disabled={locked} value={form.threshold_percentage} onChange={(e) => setForm({ ...form, threshold_percentage: parseFloat(e.target.value) || 0 })} className="rounded-sm border-brand-line w-32" />
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex items-center justify-between bg-brand-bg border border-brand-line rounded-sm p-3">
                        <div>
                            <p className="text-sm text-brand-ink font-medium">Show results before voting ends</p>
                            <p className="text-xs text-brand-inkSoft">Residents see live results while the vote is still open.</p>
                        </div>
                        <Switch data-testid="vote-show-results-switch" checked={form.show_results_before_end} onCheckedChange={(v) => setForm({ ...form, show_results_before_end: v })} />
                    </div>
                </div>

                <DialogFooter className="flex-wrap gap-2">
                    <Button type="button" variant="outline" className="rounded-full border-brand-line" onClick={() => onOpenChange(false)}>Cancel</Button>
                    {isEdit ? (
                        <Button data-testid="vote-save-btn" disabled={saving} onClick={() => submit(false)} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">Save Changes</Button>
                    ) : (
                        <>
                            <Button data-testid="vote-save-draft-btn" variant="outline" disabled={saving} onClick={() => submit(false)} className="rounded-full border-brand-line text-brand-ink">Save as Draft</Button>
                            <Button data-testid="vote-save-publish-btn" disabled={saving} onClick={() => submit(true)} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white"><CheckCircle size={14} className="mr-1" /> Create & Publish</Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Vote Card ----------
function VoteCard({ vote, flats, onEdit, onPublish, onClose, onCancel, onDelete, onViewResults }) {
    const info = typeInfo(vote.type);
    const Icon = info.icon;
    const AudIcon = AUDIENCE_ICON[vote.audience?.type] || Globe;
    const phaseMeta = PHASE_META[vote.phase] || PHASE_META.draft;
    const canEdit = vote.status !== "closed" && vote.status !== "cancelled";
    const canPublish = vote.status === "draft";
    const canCloseEarly = vote.status === "active" && vote.phase !== "closed";
    const canCancel = (vote.status === "draft" || vote.status === "active");
    const canDelete = vote.status === "draft" && (vote.total_votes || 0) === 0;
    const canViewResults = vote.status === "active" || vote.status === "closed";

    return (
        <div className="bg-white border border-brand-line rounded-sm p-4 hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-200" data-testid={`vote-card-${vote.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon size={20} weight="duotone" className="text-brand-action shrink-0" />
                    <div className="min-w-0">
                        <h3 className="font-heading text-base tracking-tight text-brand-ink truncate" data-testid={`vote-card-title-${vote.id}`}>{vote.title}</h3>
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{info.label}</p>
                    </div>
                </div>
                <Chip variant={phaseMeta.variant} data-testid={`vote-card-phase-${vote.id}`}>{phaseMeta.label}</Chip>
            </div>

            <p className="text-sm text-brand-inkSoft mb-3 line-clamp-2">{vote.description}</p>

            <div className="flex flex-wrap gap-3 text-[11px] text-brand-inkSoft mb-3">
                <span className="flex items-center gap-1"><CalendarBlank size={12} weight="duotone" /> {fmt(vote.start_date)} → {fmt(vote.end_date)}</span>
                <span className="flex items-center gap-1"><AudIcon size={12} weight="duotone" /> {audienceLabel(vote.audience, flats)}</span>
            </div>

            <div className="text-sm text-brand-ink mb-3 flex items-center gap-1">
                <strong data-testid={`vote-card-total-votes-${vote.id}`}>{vote.total_votes}</strong> / {vote.eligible_flats} eligible flats voted
            </div>

            <div className="flex gap-2 flex-wrap border-t border-brand-line pt-3">
                {canEdit && <Button size="sm" variant="outline" className="rounded-full border-brand-line" onClick={() => onEdit(vote)} data-testid={`vote-edit-btn-${vote.id}`}><PencilSimple size={13} className="mr-1" /> Edit</Button>}
                {canPublish && <Button size="sm" className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white" onClick={() => onPublish(vote.id)} data-testid={`vote-publish-btn-${vote.id}`}><CheckCircle size={13} className="mr-1" /> Publish</Button>}
                {canCloseEarly && <Button size="sm" variant="outline" className="rounded-full border-brand-line" onClick={() => onClose(vote.id)} data-testid={`vote-close-btn-${vote.id}`}><XCircle size={13} className="mr-1" /> Close Early</Button>}
                {canViewResults && <Button size="sm" variant="outline" className="rounded-full border-brand-line" onClick={() => onViewResults(vote)} data-testid={`vote-results-btn-${vote.id}`}><ChartBar size={13} className="mr-1" /> Results</Button>}
                {canCancel && <Button size="sm" variant="ghost" className="rounded-full text-brand-inkSoft" onClick={() => onCancel(vote.id)} data-testid={`vote-cancel-btn-${vote.id}`}><WarningCircle size={13} className="mr-1" /> Cancel</Button>}
                {canDelete && <Button size="sm" variant="ghost" className="rounded-full text-brand-inkSoft" onClick={() => onDelete(vote.id)} data-testid={`vote-delete-btn-${vote.id}`}><Trash size={13} className="mr-1" /> Delete</Button>}
            </div>
        </div>
    );
}

// ---------- Main Page ----------
export default function VotingManagement() {
    const [votes, setVotes] = useState([]);
    const [users, setUsers] = useState([]);
    const [flats, setFlats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [resultsVote, setResultsVote] = useState(null);

    async function loadVotes() {
        try { const r = await api.get("/votes"); setVotes(r.data); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function loadAll() {
        setLoading(true);
        try {
            const [v, u, f] = await Promise.all([api.get("/votes"), api.get("/users"), api.get("/flats")]);
            setVotes(v.data); setUsers(u.data); setFlats(f.data || []);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

    function openCreate() { setEditing(null); setFormOpen(true); }
    function openEdit(vote) { setFormOpen(true); setEditing(vote); }

    async function publishVote(id) {
        try { await api.post(`/votes/${id}/publish`); toast.success("Vote published"); loadVotes(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function closeVote(id) {
        if (!window.confirm("Close this vote now? No further ballots will be accepted.")) return;
        try { await api.post(`/votes/${id}/close`); toast.success("Vote closed"); loadVotes(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function cancelVote(id) {
        if (!window.confirm("Cancel this vote? This cannot be undone.")) return;
        try { await api.post(`/votes/${id}/cancel`); toast.success("Vote cancelled"); loadVotes(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function deleteVote(id) {
        if (!window.confirm("Delete this draft vote permanently?")) return;
        try { await api.delete(`/votes/${id}`); toast.success("Vote deleted"); loadVotes(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function toggleShowResults(vote) {
        try {
            await api.put(`/votes/${vote.id}`, { show_results_before_end: !vote.show_results_before_end });
            toast.success(!vote.show_results_before_end ? "Results are now visible to residents" : "Results hidden from residents");
            await loadVotes();
            setResultsVote(prev => prev ? { ...prev, show_results_before_end: !vote.show_results_before_end } : prev);
        } catch (e) { toast.error(formatError(e)); }
    }

    const filtered = useMemo(() => votes.filter(v => {
        if (typeFilter !== "all" && v.type !== typeFilter) return false;
        if (q.trim()) {
            const ql = q.trim().toLowerCase();
            if (!v.title.toLowerCase().includes(ql) && !v.description.toLowerCase().includes(ql)) return false;
        }
        return true;
    }), [votes, typeFilter, q]);

    const groups = [
        { key: "draft", title: "Drafts", list: filtered.filter(v => v.phase === "draft") },
        { key: "upcoming", title: "Upcoming", list: filtered.filter(v => v.phase === "upcoming") },
        { key: "active", title: "Active Voting", list: filtered.filter(v => v.phase === "active") },
        { key: "closed", title: "Closed", list: filtered.filter(v => v.phase === "closed") },
        { key: "cancelled", title: "Cancelled", list: filtered.filter(v => v.phase === "cancelled") },
    ];

    const stats = {
        total: votes.length,
        active: votes.filter(v => v.phase === "active").length,
        drafts: votes.filter(v => v.phase === "draft").length,
        closed: votes.filter(v => v.phase === "closed").length,
    };

    if (loading) return <LoadingState message="Loading votes..." />;

    return (
        <div className="animate-fade-up space-y-6" data-testid="voting-management-page">
            <PageHeader
                overline="Governance"
                title="Voting"
                description="Run elections, resolutions, and polls with flat-based, one-vote-per-household ballots."
                actions={
                    <Button data-testid="vote-create-btn" onClick={openCreate} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                        <Plus size={16} className="mr-1.5" /> Create Vote
                    </Button>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vote-summary">
                <StatCard label="Total" value={stats.total} testId="vote-summary-total" />
                <StatCard label="Active" value={stats.active} accent testId="vote-summary-active" />
                <StatCard label="Drafts" value={stats.drafts} testId="vote-summary-drafts" />
                <StatCard label="Closed" value={stats.closed} testId="vote-summary-closed" />
            </div>

            <div className="bg-white border border-brand-line rounded-sm p-4" data-testid="vote-filters">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2 relative">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="vote-search" placeholder="Search title or description..." value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm border-brand-line pl-9" />
                    </div>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger data-testid="vote-type-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            {VOTE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {votes.length === 0 ? (
                <EmptyState title="No votes created yet" description="Create your first election, poll, or resolution." />
            ) : (
                groups.filter(g => g.list.length > 0).map(g => (
                    <div key={g.key} data-testid={`vote-group-${g.key}`}>
                        <h2 className="font-heading text-lg tracking-tight text-brand-ink mb-3">{g.title}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {g.list.map(vote => (
                                <VoteCard key={vote.id} vote={vote} flats={flats}
                                    onEdit={openEdit} onPublish={publishVote} onClose={closeVote}
                                    onCancel={cancelVote} onDelete={deleteVote} onViewResults={setResultsVote} />
                            ))}
                        </div>
                    </div>
                ))
            )}

            <VoteFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} users={users} flats={flats} onSaved={loadVotes} />

            <Dialog open={!!resultsVote} onOpenChange={(v) => !v && setResultsVote(null)}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">{resultsVote?.title}</DialogTitle></DialogHeader>
                    {resultsVote && <ResultsDialog vote={resultsVote} onClose={() => setResultsVote(null)} onToggleShowResults={toggleShowResults} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}
